import {
  createContext,
  useContext,
  useState,
  useCallback,
  useRef,
  type ReactNode,
} from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle, XCircle, AlertTriangle, Info, X } from 'lucide-react';

type ToastType = 'success' | 'error' | 'warning' | 'info';

interface Toast {
  id: string;
  type: ToastType;
  title: string;
  description?: string;
  duration?: number;
}

interface ToastContextValue {
  toast: (options: Omit<Toast, 'id'>) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

const ICONS: Record<ToastType, ReactNode> = {
  success: <CheckCircle size={20} className="shrink-0 text-emerald-500 dark:text-emerald-400" />,
  error: <XCircle size={20} className="shrink-0 text-red-500 dark:text-red-400" />,
  warning: <AlertTriangle size={20} className="shrink-0 text-amber-500 dark:text-amber-400" />,
  info: <Info size={20} className="shrink-0 text-blue-500 dark:text-blue-400" />,
};

const BORDER_COLORS: Record<ToastType, string> = {
  success: 'border-l-emerald-500 dark:border-l-emerald-400',
  error: 'border-l-red-500 dark:border-l-red-400',
  warning: 'border-l-amber-500 dark:border-l-amber-400',
  info: 'border-l-blue-500 dark:border-l-blue-400',
};

const MAX_TOASTS = 5;
const DEFAULT_DURATION = 4000;

let toastCounter = 0;

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const timers = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());

  const dismiss = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
    const timer = timers.current.get(id);
    if (timer) {
      clearTimeout(timer);
      timers.current.delete(id);
    }
  }, []);

  const toast = useCallback(
    (options: Omit<Toast, 'id'>) => {
      const id = `toast-${++toastCounter}`;
      const duration = options.duration ?? DEFAULT_DURATION;

      setToasts((prev) => {
        const next = [...prev, { ...options, id }];
        if (next.length > MAX_TOASTS) {
          const removed = next.shift();
          if (removed) {
            const timer = timers.current.get(removed.id);
            if (timer) {
              clearTimeout(timer);
              timers.current.delete(removed.id);
            }
          }
        }
        return next;
      });

      if (duration > 0) {
        const timer = setTimeout(() => dismiss(id), duration);
        timers.current.set(id, timer);
      }
    },
    [dismiss],
  );

  return (
    <ToastContext.Provider value={{ toast }}>
      {children}
      <div className="fixed bottom-4 right-4 z-[9999] flex flex-col gap-2 pointer-events-none">
        <AnimatePresence mode="popLayout">
          {toasts.map((t) => (
            <motion.div
              key={t.id}
              layout
              initial={{ opacity: 0, x: 80, scale: 0.95 }}
              animate={{ opacity: 1, x: 0, scale: 1 }}
              exit={{ opacity: 0, x: 80, scale: 0.95 }}
              transition={{ type: 'spring', stiffness: 400, damping: 30 }}
              className={`pointer-events-auto flex w-80 items-start gap-3 rounded-lg border border-l-4 bg-card p-4 shadow-lg ${BORDER_COLORS[t.type]}`}
            >
              {ICONS[t.type]}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-foreground">{t.title}</p>
                {t.description && (
                  <p className="mt-0.5 text-xs text-muted-foreground">{t.description}</p>
                )}
              </div>
              <button
                onClick={() => dismiss(t.id)}
                className="shrink-0 rounded-md p-0.5 text-muted-foreground hover:text-foreground transition-colors"
              >
                <X size={14} />
              </button>
            </motion.div>
          ))}
        </AnimatePresence>
      </div>
    </ToastContext.Provider>
  );
}

export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return ctx;
}
